
import React, {  ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { documentclassification } from "../../utils/fieldConstants";
import { transformProjectDetails } from "../../utils/transformProjectDetails";
import { AccordionV2, AccordionItemV2 } from "pepsico-ds";
import "./IntakeFormFields.css";
import IntakeFormInputs from "./IntakeFormInputs/IntakeFormInputs";
import { CloseOutActions } from "../../redux/closeoutslice/closeoutSlice";

interface IntakeFormFieldsProps {
  formData: {
    [key: string]: string | number | Date | FileList | null;
  };
  handleChange: (value: any, key: string) => void;
  handleFileChange: (files: File[]) => void;
  handleClose: () => void;
  formattedProjectId: any;
  errors: { [key: string]: string | null };
  emailErrors: { [key: string]: string | null };
  inputError: { [key: string]: string | null };
  onFocus: (key: string) => void; // Add focus handler
  onBlur: (key: string) => void;
  mode: any;
  children: ReactNode;
}


interface ProjectFile {
  file_id: number;
  project_id: number;
  blob_file_name: string;
  file_name: string;
  document_classification: number;
  user_id: string;
  is_file_type_converted: number;
  is_vector_db_processed: number;
  status: boolean;
  meta_data: any;
  file_size: number;
  file_type: any;
  created_date: string | null;
}

const IntakeFormFields: React.FC<IntakeFormFieldsProps> = ({
  formData,
  handleChange,
  handleFileChange,
  handleClose,
  formattedProjectId,
  errors,
  emailErrors,
  inputError,
  onFocus,
  onBlur,
  mode,
  children,
}) => {
  const dispatch = useDispatch();

  const { errorMessage } = useSelector((state: any) => state.historical);
  const {
    isUploading,
    isFilesAdded,
    files,
    fileClassificationSelection,
    uploadedFilesSuccess,
    uploadedFiles,
    internalSelection,
    restrictedSelection,
    confidentialSelection,
  } = useSelector((state: any) => state.closeOut);
  const { historicalFormData } = useSelector((state: any) => state.closeOut);
  // Handle checkbox change for Confidential

  useEffect(() => {
    const newlyUploadedFiles = files.filter(
      (file: any) => !uploadedFilesSuccess.includes(file.name)
    );
    const filesToCompare =
      newlyUploadedFiles.length > 0 ? newlyUploadedFiles : files;
    // Check if all Confidential radio buttons are selected
    const allConfidentialSelected = filesToCompare.every(
      (file: any) =>
        confidentialSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.confidential
    );

    dispatch(CloseOutActions.setConfidentialChecked(allConfidentialSelected));

    // Check if all Restricted radio buttons are selected
    const allRestrictedSelected = filesToCompare.every(
      (file: any) =>
        restrictedSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.restricted
    );
    dispatch(CloseOutActions.setRestrictedChecked(allRestrictedSelected));

    //Check if all Internal radio buttons are selected
    const allInternalSelected = filesToCompare.every(
      (file: any) =>
        internalSelection[file.name] === "yes" ||
        file.document_classification === documentclassification.internal
    );
    dispatch(CloseOutActions.setInternalChecked(allInternalSelected));
  }, [
    dispatch,
    confidentialSelection,
    restrictedSelection,
    internalSelection,
    files,
    uploadedFilesSuccess,
  ]);

  const [localProjectData, setLocalProjectData] = useState<any>({}); // Transformed project data
  const intakeProjectDetails = useSelector(
    (state: any) => state.project.intakeProjectDetails
  );

  //
  useEffect(() => {
    if (intakeProjectDetails && intakeProjectDetails.code === 200) {
      var { projectData, files } =
        transformProjectDetails(intakeProjectDetails);
      setLocalProjectData(projectData); // Store transformed data in state

      // Transform API file data to match the format you're using
      const transformedFiles: any = files.map((apiFile) => ({
        name: apiFile.file_name, // Extract file_name from the API response
        blob_file_name: apiFile.blob_file_name, // Extract blob_file_name from the API response
        size: apiFile.file_size, // Extract file_size from the API response
        type: apiFile.file_type || "application/octet-stream", // Use file_type if available or default to 'application/octet-stream'
        document_classification: apiFile.document_classification,
        // Simulate `lastModified` and `lastModifiedDate` since API doesn't provide these
        lastModified: Date.now(), // You can use the current timestamp here
        lastModifiedDate: new Date(), // Use the current date as lastModifiedDate

        webkitRelativePath: "", // Not relevant, leave empty as per your structure
      }));

      // Merge transformed API files with existing files in state
      // setFiles((prevFiles: any) => [...prevFiles, ...transformedFiles]);
      // dispatch(addFiles(transformedFiles));
      dispatch(CloseOutActions.addFiles(transformedFiles));
      // Mark files as successfully uploaded based on `status` from API data
      const uploadedFileNames = files
        .filter((file) => file.status) // If file's `status` is true, it has been uploaded
        .map((file) => file.file_name); // Get the names of uploaded files

      // setUploadedFilesSuccess((prev) => [...prev, ...uploadedFileNames]);
      // dispatch(setUploadedFilesSuccess(uploadedFileNames));
      dispatch(CloseOutActions.setUploadedfileSuccess(uploadedFileNames));
    }
  }, [intakeProjectDetails, historicalFormData]);

  return (
    <>
      <AccordionV2
        presentation="box-shadow"
        selectedItemIndex={0}
        className="peacockAccordian"
      >
        <AccordionItemV2
          label="1. Intake & Close Out"
          presentation="box-shadow"
          className="closeoutAccordian"
        >
          <IntakeFormInputs
            formData={formData}
            mode={mode}
            errors={errors}
            emailErrors={emailErrors}
            inputErrors={inputError}
            formattedProjectId={formattedProjectId}
            handleChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            localProjectData={localProjectData}
          />
          {children}
        </AccordionItemV2>
      </AccordionV2>
    </>
  );
};

export default IntakeFormFields;
